<template>
  <v-card rounded="md" class="on-efiling-card">
    <v-card-title class="d-flex align-center justify-space-between">
      <span>
        <v-icon class="mr-2">mdi-account-switch</v-icon>
        {{ t('startTask.Officer Notification E-Filing') || 'Officer Notification - E-Filing' }}
      </span>
      <div class="d-flex align-center ga-2">
        <EfilingWalkthroughButton />
        <v-chip v-if="readOnly" color="success" variant="flat" size="small">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ t('startTask.Completed') || 'Completed' }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider />

    <v-card-text>
      <!-- Officer Notification Form -->
      <OfficerNotificationForm
        ref="onFormRef"
        :task-config="taskConfig"
        :task-id="taskId"
        :entity-cd="entityCd"
        :loading="eFiling.isSubmitting.value"
        :read-only="readOnly"
        @update:formData="handleFormDataUpdate"
        @entity-pick-request="emit('entity-pick-request')"
        @contact-pick-request="handleContactPickRequest"
        @directorship-entity-pick-request="emit('directorship-entity-pick-request')"
      />

      <!-- Validation Errors -->
      <v-alert
        v-if="!readOnly && eFiling.validationErrors.value.length > 0"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="eFiling.validationErrors.value = []"
      >
        <div class="font-weight-bold mb-2">{{ t('startTask.Validation Errors') || 'Validation Errors' }}:</div>
        <div v-for="(error, index) in eFiling.validationErrors.value" :key="index"
          :class="error.startsWith('  -') ? 'text-body-2 pl-4 text-error' : 'font-weight-medium mt-1'"
          :style="error.startsWith('  -') ? 'opacity: 0.85' : ''"
        >{{ error }}</div>
      </v-alert>

      <!-- Completed task: PDF only -->
      <v-card
        v-if="readOnly && eFiling.pdfDataBase64.value"
        variant="tonal"
        color="success"
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text-success">
            {{ t('startTask.E-Filing Completed') || 'E-Filing Completed' }}
          </span>
        </div>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleDownloadPdf"
        >
          <v-icon start>mdi-file-pdf-box</v-icon>
          {{ t('startTask.Download Receipt') || 'Download Receipt (PDF)' }}
        </v-btn>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="secondary"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="emit('back')"
      >
        <v-icon start>mdi-arrow-left</v-icon>
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!readOnly"
        color="primary"
        variant="flat"
        :loading="eFiling.isSubmitting.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="eFiling.handleSubmit(onFormRef, readOnly, (msg) => emit('error', msg))"
      >
        <v-icon start>mdi-send</v-icon>
        {{ t('startTask.Submit E-Filing') || 'Submit E-Filing' }}
      </v-btn>
    </v-card-actions>

    <!-- Credential Select Dialog -->
    <EFilingCredentialDialog
      v-model="eFiling.showCredentialDialog.value"
      :mycoid-credentials="eFiling.mycoidCredentials.value"
      :selected-id="eFiling.selectedCredentialId.value"
      :is-loading="eFiling.isLoadingCredentials.value"
      :guide-message="t('startTask.ON Credential Required') || 'credential is required for Officer Notification E-Filing.'"
      @select="eFiling.selectCredential"
      @confirm="eFiling.handleCredentialConfirm(readOnly)"
      @manage="eFiling.openManageCredential"
    />

    <!-- Credential Manage Modal -->
    <CredentialManageModal
      v-model="eFiling.showManageCredentialDialog.value"
      @credential-added="eFiling.handleCredentialAdded"
    />

    <!-- Confirm Dialog -->
    <EFilingConfirmDialog
      v-model="eFiling.showConfirmDialog.value"
      :credential="eFiling.selectedCredential.value"
      :message="t('startTask.ON Submit Confirm Message') || 'Are you sure you want to submit this Officer Notification e-filing?'"
      @confirm="handleConfirmSubmit"
    />

    <!-- Process Dialog -->
    <EFilingProcessDialog
      v-model="eFiling.showProcessDialog.value"
      :step="eFiling.submitStep.value"
      :success="eFiling.resultSuccess.value"
      :message="eFiling.resultMessage.value"
      :pdf-data-base64="eFiling.pdfDataBase64.value"
      :warning-message="eFiling.warningMessage.value"
      :success-title="t('startTask.ON E-Filing Success') || 'Officer Notification Submitted Successfully!'"
      :fail-title="t('startTask.ON E-Filing Failed') || 'Officer Notification Failed'"
      @close="handleProcessDialogClosed"
      @download-pdf="handleDownloadPdf"
    >
      <template #result>
        <div class="text-body-1 text-grey-darken-1 mt-2">
          {{ t('startTask.ON Submit Success Message') || 'Officer notification has been submitted successfully. Download the receipt below.' }}
        </div>
      </template>
    </EFilingProcessDialog>

  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import OfficerNotificationForm from './OfficerNotificationForm.vue';
import { officerNotificationApi } from '../../api/officerNotificationApi';
import { buildAppointPayload, buildChangePayload, buildCeasePayload } from './composables/useFormPayload';
import { updateEFilingComplete, saveEFilingData } from '@/features/startTask/api/taskApi';
import CredentialManageModal from '@/features/Entity_my/components/credentials/CredentialManageModal.vue';
import { useEFilingSubmit } from '../../composables/useEFilingSubmit';
import { downloadPdf } from '../../composables/usePdfDownload';
import EFilingCredentialDialog from '../shared/EFilingCredentialDialog.vue';
import EfilingWalkthroughButton from '../shared/EfilingWalkthroughButton.vue';
import EFilingConfirmDialog from '../shared/EFilingConfirmDialog.vue';
import EFilingProcessDialog from '../shared/EFilingProcessDialog.vue';

const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  entityCd?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
  taskConfig?: any;
  // Standalone 흐름: 좌측 챗봇에서 선택된 entity (StandaloneEFilingPage 가 주입).
  selectedEntity?: Record<string, any> | null;
  // 챗봇에서 선택된 contact → 폼 자동 채움.
  selectedContact?: Record<string, any> | null;
  // 챗봇에서 선택된 directorship entity → Other Directorship 자동 채움.
  selectedDirectorshipEntity?: Record<string, any> | null;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  entityCd: '',
  showBackButton: true,
  readOnly: false,
  taskConfig: null,
  selectedEntity: null,
  selectedContact: null,
  selectedDirectorshipEntity: null,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [message: string];
  // 좌측 챗봇에 entity-selector 위젯을 띄워달라는 부모 신호.
  'entity-pick-request': [];
  'contact-pick-request': [entityCd: string];
  'directorship-entity-pick-request': [];
}>();

// Composable
const eFiling = useEFilingSubmit({ formType: 'M32003' });

// Form ref
const onFormRef = ref<InstanceType<typeof OfficerNotificationForm> | null>(null);
const formDataRef = ref<any>(null);

// Stored success data — emit('submit') is deferred until process dialog closes.
const successApiData = ref<any>(null);

// Load saved data on mount
onMounted(() => {
  if (props.taskData?.formData) {
    setTimeout(() => {
      if (onFormRef.value?.loadSavedData) {
        onFormRef.value.loadSavedData(props.taskData.formData);
      }
    }, 100);
  }

  if (props.readOnly && props.taskData?.eFilingResult) {
    const result = props.taskData.eFilingResult;
    eFiling.pdfDataBase64.value = result.pdfDataBase64 || result.pdf_base64 || null;
    eFiling.warningMessage.value = result.warningMessage || null;
  }
});

watch(() => props.taskData, (newData) => {
  if (newData?.formData && onFormRef.value?.loadSavedData) {
    onFormRef.value.loadSavedData(newData.formData);
  }
}, { deep: true });

// ── 챗봇에서 선택된 entity → 폼 자동 채움 (ChangeAddressEFiling 과 동일 패턴) ──
watch(
  () => props.selectedEntity,
  async (entity) => {
    if (!entity) return;
    await nextTick();
    let attempts = 0;
    const maxAttempts = 20; // ~1초
    while (attempts < maxAttempts) {
      const fn = onFormRef.value?.applyEntity;
      if (typeof fn === 'function') {
        try {
          fn(entity);
          console.log('[OfficerNotificationEFiling] applyEntity called', entity);
        } catch (e) {
          console.error('[OfficerNotificationEFiling] applyEntity threw', e);
        }
        return;
      }
      attempts++;
      await new Promise((r) => setTimeout(r, 50));
    }
    console.warn('[OfficerNotificationEFiling] onFormRef.applyEntity not ready after retries');
  },
  { deep: true, immediate: true },
);

// ── 챗봇에서 선택된 contact → 폼 자동 채움 ──
watch(
  () => props.selectedContact,
  async (contact) => {
    if (!contact) return;
    await nextTick();
    let attempts = 0;
    const maxAttempts = 20;
    while (attempts < maxAttempts) {
      const fn = onFormRef.value?.applyContact;
      if (typeof fn === 'function') {
        try {
          fn(contact);
          console.log('[OfficerNotificationEFiling] applyContact called', contact);
        } catch (e) {
          console.error('[OfficerNotificationEFiling] applyContact threw', e);
        }
        return;
      }
      attempts++;
      await new Promise((r) => setTimeout(r, 50));
    }
    console.warn('[OfficerNotificationEFiling] onFormRef.applyContact not ready after retries');
  },
  { deep: true },
);

// ── 챗봇에서 선택된 directorship entity → Other Directorship 폼 자동 채움 ──
watch(
  () => props.selectedDirectorshipEntity,
  async (entity) => {
    if (!entity) return;
    await nextTick();
    let attempts = 0;
    const maxAttempts = 20;
    while (attempts < maxAttempts) {
      const fn = onFormRef.value?.applyDirectorshipEntity;
      if (typeof fn === 'function') {
        try {
          fn(entity);
          console.log('[OfficerNotificationEFiling] applyDirectorshipEntity called', entity);
        } catch (e) {
          console.error('[OfficerNotificationEFiling] applyDirectorshipEntity threw', e);
        }
        return;
      }
      attempts++;
      await new Promise((r) => setTimeout(r, 50));
    }
    console.warn('[OfficerNotificationEFiling] onFormRef.applyDirectorshipEntity not ready after retries');
  },
  { deep: true },
);

// ── Contact pick → 부모 ConversationPanel 에 위임 ──
function handleContactPickRequest(entityCd: string) {
  const ec = entityCd || props.entityCd || '';
  emit('contact-pick-request', ec);
}

const handleFormDataUpdate = (data: any) => {
  formDataRef.value = data;
};

/**
 * Process dialog closed → emit 'submit' to parent if success, so parent can navigate.
 * On failure the dialog just closes and user stays on the form.
 */
function handleProcessDialogClosed() {
  eFiling.handleProcessDialogClose(null, () => {
    // Called only when isSubmitSuccess === true
    emit('submit', successApiData.value);
    successApiData.value = null;
  });
}

const handleDownloadPdf = () => {
  if (eFiling.pdfDataBase64.value) {
    downloadPdf(eFiling.pdfDataBase64.value, 'OfficerNotification_receipt');
  }
};

// Officer Notification specific submit logic
const handleConfirmSubmit = async () => {
  if (props.readOnly) {
    eFiling.showConfirmDialog.value = false;
    return;
  }

  const formData = onFormRef.value?.getFormData();
  if (!formData) {
    eFiling.validationErrors.value = ['Failed to get form data'];
    emit('error', 'Failed to get form data');
    return;
  }

  const action = formData._action;
  const officerType = formData._officerType;

  eFiling.startProcessing();

  try {
    // Step 1: SSM Login
    const loginResponse: any = await officerNotificationApi.ssmLogin(eFiling.selectedCredentialId.value || '');

    if (!loginResponse.success || !loginResponse.session_id) {
      throw new Error(loginResponse.error || 'SSM Login failed. Please check your MYCOID2016 credential.');
    }

    const sessionId = loginResponse.session_id;

    // Step 2: Build payload and call appropriate API
    let response: any;

    if (action === 'appoint') {
      const payload = await buildAppointPayload(formData, sessionId, officerType);
      response = await officerNotificationApi.appointOfficer(payload);
    } else if (action === 'change') {
      const payload = await buildChangePayload(formData, sessionId, officerType);
      response = await officerNotificationApi.changeOfficer(payload);
    } else if (action === 'cease') {
      const payload = await buildCeasePayload(formData, sessionId, officerType);
      response = await officerNotificationApi.ceaseOfficer(payload);
    } else {
      throw new Error(`Unknown action: ${action}`);
    }

    const apiData = (response as any)?.data || response;

    if (apiData?.success) {
      eFiling.pdfDataBase64.value = apiData.pdf_base64 || apiData.pdfDataBase64 || null;
      eFiling.warningMessage.value = apiData.warning || null;

      eFiling.completeSuccess('Officer notification submitted successfully!');

      if (props.taskId) {
        try {
          await updateEFilingComplete(props.taskId);
        } catch (updateError) {
          console.error('[OfficerNotificationEFiling] Task status update failed:', updateError);
        }
      }
      try {
        await saveEFilingData(props.taskId ?? null, {
          taskTypeCd: 'M32003',
          entityCd: props.entityCd || props.selectedEntity?.entityCd || props.selectedEntity?.ENTITY_CD || null,
          countryCd: 'MY',
          formData: formData as Record<string, unknown>,
          responseData: apiData as Record<string, unknown>,
          warningMessage: eFiling.warningMessage.value || null,
          receiptPdfBase64: eFiling.pdfDataBase64.value || null,
          receiptPdfFilename: 'OfficerNotification_receipt.pdf',
          status: 'B36003',
          isSuccess: true,
          completedAt: new Date().toISOString(),
        });
      } catch (saveError) {
        console.error('[OfficerNotificationEFiling] L_TASK_EFILING_DATA save failed:', saveError);
      }

      // Don't emit('submit') here — defer until user closes the process dialog
      // so they can see the success result and download the PDF receipt.
      successApiData.value = apiData;
    } else {
      eFiling.completeFromApiResult(apiData, '', 'Officer notification failed');
    }
  } catch (error: any) {
    const errorMessage = eFiling.completeError(error, 'Failed to submit officer notification');
    emit('error', errorMessage);
  }
};
</script>

<style scoped>
.on-efiling-card {
  margin: 8px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
</style>
